import React, { useState } from "react";
import { Phone, Mail, CalendarCheck, Send } from "lucide-react";
import { CALENDLY_URL, PHONE, PHONE_TEL, EMAIL } from "@/lib/siteData";
import { useToast } from "@/hooks/use-toast";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import { trackLead } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", consent_request: false, consent_marketing_phone: false, consent_marketing_email: false };

export default function Contact() {
  const [data, setData] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) {
      toast({ title: "Champs requis", description: "Nom, email et message sont obligatoires.", variant: "destructive" });
      return;
    }
    if (data.consent_request && !data.phone.trim()) {
      toast({ title: "Téléphone requis", description: "Indiquez votre numéro pour être rappelé, ou décochez la case de rappel.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      trackLead();
      toast({ title: "Message envoyé ✨", description: "Maël vous répond très vite." });
      setData(EMPTY);
    } catch {
      toast({ title: "Erreur", description: "Réessayez ou appelez le 06.88.07.38.33.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-md-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-4">Contact</div>
          <h2 className="font-serif text-4xl md:text-5xl text-md-primary font-medium leading-tight mb-6">
            On en parle <span className="italic">autour d'un café ?</span>
          </h2>
          <p className="text-md-muted leading-relaxed mb-10">
            Plusieurs canaux pour me joindre, choisissez celui qui vous convient le mieux. Tous mes rendez-vous sont gratuits et sans engagement.
          </p>

          <div className="space-y-4">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="contact-calendly" className="flex items-center gap-4 p-5 bg-md-primary text-white rounded-2xl hover:bg-md-primaryHover transition-colors group">
              <div className="w-12 h-12 rounded-xl bg-md-secondary grid place-items-center"><CalendarCheck className="w-5 h-5 text-white" /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-md-secondary mb-1">Le plus rapide</div>
                <div className="font-semibold">Réserver un créneau Calendly</div>
              </div>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </a>
            <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="flex items-center gap-4 p-5 bg-white border border-md-border rounded-2xl hover:border-md-secondary transition-colors">
              <div className="w-12 h-12 rounded-xl bg-md-secondary/15 grid place-items-center"><Phone className="w-5 h-5 text-md-secondary" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-md-accent mb-1">Téléphone</div>
                <div className="font-semibold text-md-primary">{PHONE}</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-4 p-5 bg-white border border-md-border rounded-2xl hover:border-md-secondary transition-colors">
              <div className="w-12 h-12 rounded-xl bg-md-accent/15 grid place-items-center"><Mail className="w-5 h-5 text-md-accent" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-md-accent mb-1">Email</div>
                <div className="font-semibold text-md-primary">{EMAIL}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white p-8 md:p-10 rounded-3xl border border-md-border shadow-sm space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Input label="Votre nom *" testId="contact-name" value={data.name} onChange={(v) => setData({ ...data, name: v })} />
              <Input label="Email *" testId="contact-form-email" type="email" value={data.email} onChange={(v) => setData({ ...data, email: v })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Input label="Téléphone" testId="contact-form-phone" value={data.phone} onChange={(v) => setData({ ...data, phone: v })} />
              <Input label="Sujet" testId="contact-subject" value={data.subject} onChange={(v) => setData({ ...data, subject: v })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-md-accent mb-2 block">Votre message *</label>
              <textarea
                data-testid="contact-message"
                rows={5}
                value={data.message}
                onChange={(e) => setData({ ...data, message: e.target.value })}
                placeholder="Dites-moi en quelques mots ce dont vous avez besoin…"
                className="w-full bg-md-bg border border-md-border rounded-xl p-4 text-md-text focus:outline-none focus:border-md-secondary transition-colors"
              />
            </div>
            <ConsentCheckboxes
              source="contact"
              testPrefix="contact"
              value={{ request: data.consent_request, marketing_phone: data.consent_marketing_phone, marketing_email: data.consent_marketing_email }}
              onChange={(v) => setData({ ...data, consent_request: v.request, consent_marketing_phone: v.marketing_phone, consent_marketing_email: v.marketing_email })}
            />
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="inline-flex items-center gap-2 bg-md-primary hover:bg-md-primaryHover disabled:opacity-50 text-white px-7 py-4 rounded-full font-semibold transition-all hover:-translate-y-0.5"
            >
              {sending ? "Envoi…" : "Envoyer mon message"} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Input({ label, testId, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-md-accent mb-2 block">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-md-bg border border-md-border rounded-xl p-4 text-md-text focus:outline-none focus:border-md-secondary transition-colors"
      />
    </label>
  );
}
